import React, { useState } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { Coordinates, AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { ListingEntry } from '../ListingsPanel/components/ListingEntry';
import { useSwipeLeftRight } from '@shared/hooks/useSwipeLeftRight';
import { ReactComponent as IconSwipeRight } from '../../assets/swipe-icon.svg';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { useOuterClick } from '@shared/utils/hooks/useOuterClick';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

interface SingleHotelViewProps {
  isOpen: boolean;
  onClose: () => void;
  listings: Array<AccommodationListingDTO>;
  selectedHotelId: Maybe<string>;
  venueCoordinates: Coordinates | null;
  state: AccommodationsConsoleState;
  noOfNights: number;
}

export const SingleHotelView = (props: SingleHotelViewProps) => {
  const { isOpen, onClose, listings, selectedHotelId, venueCoordinates, state, noOfNights } = props;
  const { t } = useTranslation('accommodationsConsole');
  const singleHotelViewTrans = t('singleHotelView');

  const viewBoxRef = useOuterClick(onClose, isOpen);
  const [currentIndex, setCurrentIndex] = useState(() => {
    const index = listings.findIndex(listing => listing.joyPlaceId === selectedHotelId);
    return index >= 0 ? index : 0;
  });

  const goToNext = useEventCallback(() => {
    setCurrentIndex(prev => (prev + 1) % listings.length);
  });

  const goToPrevious = useEventCallback(() => {
    setCurrentIndex(prev => (prev - 1 + listings.length) % listings.length);
  });

  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipeLeftRight({ minSwipeDistance: 50 }, goToNext, goToPrevious);

  const currentHotel = listings[currentIndex];

  if (!currentHotel) return null;

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      backgroundColor="white"
      maxHeight={'97%'}
      borderTopLeftRadius={pxToRem(12)}
      borderTopRightRadius={pxToRem(12)}
      boxShadow="0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)"
      overflowY="auto"
      zIndex={1000}
      ref={viewBoxRef}
    >
      <Box paddingX={6} paddingY={4}>
        {listings.length > 1 && (
          <Flex
            alignItems="center"
            paddingBottom={6}
            paddingTop={5}
            gap={2}
            justifyContent="flex-end"
            width="100%"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <IconSwipeRight />
            <TextV2 typographyVariant="label2" color="mono10">
              {singleHotelViewTrans.swipeRightForQuickView()}
            </TextV2>
          </Flex>
        )}
        <ListingEntry state={state} venueCoordinates={venueCoordinates} listing={currentHotel} noOfNights={noOfNights} />
      </Box>
    </Box>
  );
};
